import { Button, Pill, TableCell, TableRow } from '@/shared/components';
import type { OutboxFilterStatus, OutboxMessageResponse } from '@/shared/api';
import { formatDateTime } from '@/shared/lib/format';
import { outboxStatusLabel } from './systemLabels';

export const statusVariants: Record<
  OutboxFilterStatus,
  'accent' | 'warning' | 'error' | 'outline'
> = {
  Pending: 'outline',
  Processed: 'accent',
  Failed: 'error',
  All: 'outline',
};

/** Ab acht Versuchen gibt der Versand auf — dieselbe Grenze wie im Hintergrunddienst. */
export const MAX_ATTEMPTS = 8;

export function derivedStatus(message: OutboxMessageResponse): OutboxFilterStatus {
  if (message.processedAt) return 'Processed';
  return message.attempts >= MAX_ATTEMPTS ? 'Failed' : 'Pending';
}

interface Props {
  message: OutboxMessageResponse;
  retrying: boolean;
  onRetry: () => void;
}

/**
 * Eine Zeile des Mail-Ausgangs.
 *
 * <p>Drei Befunde aus Sprint 55: der Grund des Scheiterns (`lastError`) kam im Vertrag an und
 * wurde nie angezeigt — der Zustand „Gescheitert" ließ sich also nicht beantworten, ohne in die
 * Datenbank zu sehen. Der Ladezustand hing an der Mutation statt an der Zeile, also drehten beim
 * Wiederholen alle Knöpfe der Seite gleichzeitig. Und „Erneut senden" stand auch an offenen
 * Nachrichten, die der Endpunkt gar nicht annimmt.</p>
 */
export function OutboxRow({ message, retrying, onRetry }: Props) {
  const status = derivedStatus(message);

  return (
    <TableRow>
      <TableCell>
        <Pill variant={statusVariants[status]} size="sm">
          {outboxStatusLabel(status)}
        </Pill>
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono text-xs">
        {/* Ausgelöst und versendet teilen sich eine Spalte — beides gleichzeitig gibt es nie. */}
        {message.processedAt
          ? formatDateTime(message.processedAt)
          : formatDateTime(message.occurredAt)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums">
        {message.attempts}
        <span className="text-ink-faint"> / {MAX_ATTEMPTS}</span>
      </TableCell>
      <TableCell
        className={message.lastError ? 'text-sealwax max-w-xs truncate text-xs' : 'text-xs'}
        title={message.lastError ?? undefined}
      >
        {message.lastError ?? '—'}
      </TableCell>
      <TableCell className="text-right">
        {/* Der Endpunkt nimmt nur endgültig Gescheiterte an (`OutboxReadController.Retry`:
            `DeadLetteredAt != null || Attempts >= MaxAttempts`), sonst 404. */}
        {status === 'Failed' && (
          <Button variant="ghost" className="min-h-11" loading={retrying} onClick={onRetry}>
            Erneut senden
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
